import { useEffect, useRef } from "react";
import { isTopBottomSliderSwipe } from "../bottomSliderSwipeStack";

export type UseSwipeOptions = {
    /** When false, no document listeners (e.g. slider closed). */
    enabled?: boolean;
    /** If set, swipe only counts when this slider is the top of the open stack. */
    stackId?: number;
};

/** Spread on slider wrapper; swipe is handled on document. */
function noopTouch() {}

const useSwipe = (onSwipeDown: () => void, options?: UseSwipeOptions) => {
    const { enabled = true, stackId } = options ?? {};
    const onSwipeRef = useRef(onSwipeDown);
    onSwipeRef.current = onSwipeDown;

    const xDownRef = useRef<number | null>(null);
    const yDownRef = useRef<number | null>(null);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const getTouches = (evt: TouchEvent) => evt.touches;

        const onTouchStart = (evt: TouchEvent) => {
            if (stackId !== undefined && !isTopBottomSliderSwipe(stackId)) {
                return;
            }
            const firstTouch = getTouches(evt)[0];
            if (!firstTouch) {
                return;
            }
            xDownRef.current = firstTouch.clientX;
            yDownRef.current = firstTouch.clientY;
        };

        const onTouchMove = (evt: TouchEvent) => {
            if (stackId !== undefined && !isTopBottomSliderSwipe(stackId)) {
                return;
            }
            if (xDownRef.current == null || yDownRef.current == null) {
                return;
            }
            const touch = evt.touches[0];
            if (!touch) {
                return;
            }

            const xUp = touch.clientX;
            const yUp = touch.clientY;

            const xDiff = xDownRef.current - xUp;
            const yDiff = yDownRef.current - yUp;

            if (Math.abs(xDiff) > Math.abs(yDiff)) {
                /* horizontal — ignore */
            } else if (yDiff < 0) {
                /* down swipe */
                onSwipeRef.current();
            }
            xDownRef.current = null;
            yDownRef.current = null;
        };

        document.addEventListener("touchstart", onTouchStart, false);
        document.addEventListener("touchmove", onTouchMove, false);
        return () => {
            document.removeEventListener("touchstart", onTouchStart, false);
            document.removeEventListener("touchmove", onTouchMove, false);
        };
    }, [enabled, stackId]);

    return {
        onTouchStart: noopTouch,
        onTouchMove: noopTouch,
    };
};

export default useSwipe;
